import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import Reveal from '@/components/base/Reveal';
import { testimonials } from '@/mocks/site';

const OUT_MS = 140;

export default function Testimonials() {
  const [active, setActive] = useState(0);
  const [shown, setShown] = useState(0);
  const [phase, setPhase] = useState<'in' | 'out'>('in');
  const timer = useRef<number | null>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const stripRef = useRef<HTMLDivElement | null>(null);

  const revealTab = (i: number) => {
    const strip = stripRef.current;
    const tab = tabRefs.current[i];
    if (!strip || !tab || strip.scrollWidth <= strip.clientWidth) return;
    const left = tab.offsetLeft - (strip.clientWidth - tab.offsetWidth) / 2;
    strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  };

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);

  const select = (i: number) => {
    if (i === active) return;
    setActive(i);
    revealTab(i);
    if (timer.current) window.clearTimeout(timer.current);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setShown(i);
      setPhase('in');
      return;
    }
    setPhase('out');
    timer.current = window.setTimeout(() => {
      setShown(i);
      setPhase('in');
      timer.current = null;
    }, OUT_MS);
  };

  const onKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    let next = -1;
    if (e.key === 'ArrowRight') next = (i + 1) % testimonials.length;
    if (e.key === 'ArrowLeft') next = (i - 1 + testimonials.length) % testimonials.length;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = testimonials.length - 1;
    if (next < 0) return;
    e.preventDefault();
    select(next);
    tabRefs.current[next]?.focus();
  };

  const t = testimonials[shown];

  return (
    <section className="py-16 md:py-[96px] bg-background-100 overflow-hidden">
      <div className="mx-auto max-w-[1280px] px-5 md:px-8 lg:px-10">
        <Reveal>
          <h2 className="font-heading uppercase text-center text-[30px] md:text-[44px] leading-[0.95]">What owners say</h2>
        </Reveal>

        <div className="mx-auto max-w-[790px] mt-7 md:mt-12 md:min-h-[170px] flex items-center justify-center">
          <figure
            id="testimonial-panel"
            role="tabpanel"
            aria-live="polite"
            className={`text-center transition-[opacity,transform] ease-out motion-reduce:transition-none ${
              phase === 'in' ? 'opacity-100 translate-y-0 duration-300' : 'opacity-0 -translate-y-1 duration-150'
            }`}
          >
            <span className="mx-auto block w-8 h-[2px] bg-primary-500" aria-hidden="true" />
            <blockquote className="mt-5 md:mt-6 text-[19px] sm:text-[22px] md:text-[27px] leading-[1.45] text-foreground-950">
              “{t.quote}”
            </blockquote>
            <figcaption className="mt-4 md:mt-5 text-[11px] md:text-xs uppercase tracking-[0.18em] text-foreground-600">
              {t.name} <span className="mx-2 text-primary-500">/</span> {t.vehicle}
            </figcaption>
          </figure>
        </div>

        <div ref={stripRef} className="mt-8 md:mt-12 -mx-5 px-5 md:-mx-4 md:px-4 overflow-x-auto overscroll-x-contain snap-x snap-mandatory scroll-px-5 md:snap-none touch-pan-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div role="tablist" aria-label="Customer reviews" className="flex w-max mx-auto items-stretch">
            {testimonials.map((item, i) => {
              const isActive = i === active;
              return (
                <button
                  key={item.name}
                  ref={(el) => { tabRefs.current[i] = el; }}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls="testimonial-panel"
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => select(i)}
                  onKeyDown={(e) => onKey(e, i)}
                  style={{ clipPath: 'polygon(var(--cut) 0, 100% 0, calc(100% - var(--cut)) 100%, 0 100%)' }}
                  className={`relative snap-start shrink-0 -ml-[6px] first:ml-0 w-[calc((100vw-40px)/2+3px)] max-w-[200px] sm:w-[200px] md:w-[220px] md:max-w-none [--cut:14px] md:[--cut:18px] h-[62px] md:h-[64px] px-[24px] md:px-8 flex flex-col items-center justify-center text-center md:whitespace-nowrap cursor-pointer transition-colors duration-200 outline-none focus-visible:brightness-110 focus-visible:underline ${
                    isActive
                      ? 'bg-primary-500 text-foreground-950'
                      : 'bg-foreground-900 text-background-50 hover:bg-foreground-800'
                  }`}
                >
                  <span className="max-w-full text-[12px] md:text-[14px] leading-[1.2] font-semibold uppercase tracking-normal md:tracking-wide break-words">{item.name}</span>
                  <span className={`mt-0.5 text-[10px] md:text-[11px] whitespace-nowrap ${isActive ? 'text-foreground-950/80' : 'text-background-50/60'}`}>
                    Review {String(i + 1).padStart(2, '0')}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <p className="mt-4 md:mt-5 text-center text-[11px] tracking-[0.2em] text-foreground-600" aria-hidden="true">
          <span className="text-primary-500">{String(active + 1).padStart(2, '0')}</span> / {String(testimonials.length).padStart(2, '0')}
        </p>
        <p className="mt-2 text-center text-[10px] uppercase tracking-[0.18em] text-foreground-500">Illustrative reviews for demonstration</p>
      </div>
    </section>
  );
}